/**
 * Demo sandbox persona switch (docs/features/demo-sandbox/plan.md §4.4).
 *
 * Switching stores the persona's tokens through the normal session store and reloads the app, so
 * every context starts clean and the usual session restore (refresh token → new tokens) runs; a
 * guardian persona lands in the family portal like any guardian. The owner's refresh token is kept
 * for "Back to my account" (this tab only). Sessions are per tab, so other tabs are not affected.
 */
import { TokenService } from '../auth/token.service';
import SandboxService from './sandbox.service';
import { clearPersonaState, getActivePersona, getOwnerRefreshToken, setActivePersona, stashOwnerSession } from '../../utils/sandbox';

const reloadAt = (path: string) => window.location.assign(path);

export async function switchToPersona(key: string, navigate: (path: string) => void = reloadAt): Promise<void> {
  // The first switch keeps the owner's session; later switches (persona → persona) keep that one
  if (!getActivePersona()) {
    const ownerRefresh = TokenService.getRefreshToken();
    if (ownerRefresh) stashOwnerSession(ownerRefresh);
  }
  const { tokens, persona } = await SandboxService.personaLogin(key);
  TokenService.saveTokens({ accessToken: tokens.accessToken, idToken: tokens.idToken, refreshToken: tokens.refreshToken });
  setActivePersona({ key: persona.key, name: persona.name, title: persona.title, portal: persona.portal });
  navigate(persona.portal ? '/portal' : '/');
}

/** Back to the owner's own session; without a stash (e.g. storage cleared) the owner signs in again. */
export function backToOwner(navigate: (path: string) => void = reloadAt): void {
  const ownerRefresh = getOwnerRefreshToken();
  clearPersonaState();
  TokenService.clearTokens();
  if (ownerRefresh) {
    TokenService.saveTokens({ refreshToken: ownerRefresh });
    navigate('/');
  } else {
    navigate('/login');
  }
}
